import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Link,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { InputAdornment, IconButton } from '@mui/material';
import { PageShell } from '../components/layout';
import api from '../services/api'; // Use centralized API service

const ResetPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);

  // Strip token from URL immediately to prevent Referer header leakage
  useEffect(() => {
    if (token) {
      window.history.replaceState({}, '', '/reset-password');
    }
  }, [token]);

  // Check password strength in real-time
  useEffect(() => {
    if (password) {
      checkPasswordStrength(password);
    } else {
      setPasswordStrength(null);
    }
  }, [password]);

  const checkPasswordStrength = async (pwd) => {
    try {
      const response = await api.post('/password/check-strength', { password: pwd });
      const data = response.data || response;
      setPasswordStrength(data);
    } catch (error) {
      console.error('Password strength check failed:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset token. Please request a new password reset.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const response = await api.post('/auth/reset-password', {
        token: token,
        new_password: password
      });

      const data = response.data || response;
      setSuccess(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 'Strong': return 'success';
      case 'Medium': return 'warning';
      case 'Weak': return 'error';
      default: return 'default';
    }
  };

  const getPasswordStrengthProgress = (score, maxScore) => {
    return (score / maxScore) * 100;
  };

  // Check if token exists
  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  return (
    <PageShell hideChat kind="form" dots containerSx={{ py: { xs: 2, sm: 8 }, justifyContent: 'center' }}>
      <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <RouterLink to="/" style={{ textDecoration: 'none' }}>
          <Typography
            variant="h3"
            component="div"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              ...theme.modern.utils.gradientText('primary'),
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Reset Your Password
          </Typography>
        </RouterLink>

        <Typography 
          variant="body1" 
          sx={{ 
            ...theme.modern.typography.modernSubtext 
          }}
        >
          {success ? 'Password reset successful!' : 'Enter your new password below'}
        </Typography>
      </Box>

      <Paper
        variant="modern"
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {success ? (
          <Box textAlign="center">
            <Alert severity="success" sx={{ mb: 3 }}>
              Your password has been successfully reset!
            </Alert>
            <Typography variant="body1" sx={{ mb: 3 }}>
              You can now login with your new password.
            </Typography>
            <Button
              variant="gradient"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                py: 1.5,
                px: 4,
              }}
            >
              Go to Login
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting || !token}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.5rem',
                    },
                  }}
                />

                {/* Password Strength Indicator */}
                {passwordStrength && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Password Strength:
                      </Typography>
                      <Chip 
                        label={passwordStrength.strength}
                        color={getPasswordStrengthColor(passwordStrength.strength)}
                        size="small"
                      />
                    </Box>
                        
                    <LinearProgress
                      variant="determinate"
                      value={getPasswordStrengthProgress(passwordStrength.score, passwordStrength.max_score)}
                      color={getPasswordStrengthColor(passwordStrength.strength)}
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        mb: 1 
                      }}
                    />

                    {/* Password Requirements */}
                    <Box sx={{ mt: 1 }}>
                      {Object.entries(passwordStrength.checks).map(([check, passed]) => {
                        const checkLabels = {
                          length_ok: 'At least 8 characters',
                          has_uppercase: 'Uppercase letter (A-Z)',
                          has_lowercase: 'Lowercase letter (a-z)', 
                          has_numbers: 'Number (0-9)',
                          has_special: 'Special character (!@#$%^&*)',
                          no_common_patterns: 'No common patterns'
                        };

                        return (
                          <Box key={check} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {passed ? (
                              <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                            ) : (
                              <CancelIcon color="error" sx={{ fontSize: 16 }} />
                            )}
                            <Typography 
                              variant="body2" 
                              color={passed ? 'success.main' : 'error.main'}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {checkLabels[check]}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Recommendations */}
                    {passwordStrength.recommendations && passwordStrength.recommendations.length > 0 && (
                      <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          <strong>Suggestions:</strong> {passwordStrength.recommendations.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting || !token}
                  error={confirmPassword && password !== confirmPassword}
                  helperText={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.5rem',
                    },
                  }}
                />
              </Grid>

              <Grid size={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="gradient"
                  size="large"
                  disabled={
                    isSubmitting || 
                    !token || 
                    !password || 
                    !confirmPassword || 
                    password !== confirmPassword ||
                    (passwordStrength && !passwordStrength.is_secure)
                  }
                  sx={{
                    py: 1.5,
                  }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Remember your password?{' '}
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            underline="hover"
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            Back to login
          </Link>
        </Typography>
      </Box>
    </PageShell>
  );
};

export default ResetPassword;